import { getSupabaseBrowserClient } from "@/lib/supabase/client";
import { createId, safeStorageExtension } from "@/lib/utils";
import {
  BANNER_IMAGE_TYPES,
  MAX_BANNER_BYTES,
  SITE_MEDIA_BUCKET,
  type AdminSidebarBanner,
  type SidebarBanner,
} from "@/lib/banner/types";

async function signSiteMedia(path: string | null): Promise<string | null> {
  if (!path) {
    return null;
  }

  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.storage
    .from(SITE_MEDIA_BUCKET)
    .createSignedUrl(path, 60 * 60);

  if (error) {
    return null;
  }

  return data.signedUrl;
}

export async function getSidebarBanner(): Promise<SidebarBanner | null> {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.rpc("get_sidebar_banner");

  if (error) {
    throw error;
  }

  if (!data || typeof data !== "object") {
    return null;
  }

  const payload = data as Record<string, unknown>;
  if (typeof payload.imagePath !== "string" || typeof payload.targetUrl !== "string") {
    return null;
  }

  return {
    imagePath: payload.imagePath,
    imageUrl: await signSiteMedia(payload.imagePath),
    targetUrl: payload.targetUrl,
    altText: typeof payload.altText === "string" ? payload.altText : null,
  };
}

export async function getAdminSidebarBanner(): Promise<AdminSidebarBanner> {
  const supabase = getSupabaseBrowserClient();
  const { data, error } = await supabase.rpc("get_admin_sidebar_banner");

  if (error) {
    throw error;
  }

  const payload =
    data && typeof data === "object" ? (data as Record<string, unknown>) : {};
  const imagePath =
    typeof payload.imagePath === "string" ? payload.imagePath : null;

  return {
    imagePath,
    imageUrl: await signSiteMedia(imagePath),
    targetUrl: typeof payload.targetUrl === "string" ? payload.targetUrl : null,
    altText: typeof payload.altText === "string" ? payload.altText : null,
    isActive: payload.isActive === true,
  };
}

export async function uploadSidebarBannerImage(input: {
  userId: string;
  file: File;
}) {
  if (!BANNER_IMAGE_TYPES.includes(input.file.type as (typeof BANNER_IMAGE_TYPES)[number])) {
    throw new Error("Csak JPG, PNG vagy WebP kép tölthető fel.");
  }
  if (input.file.size > MAX_BANNER_BYTES) {
    throw new Error("A bannerkép legfeljebb 8 MB lehet.");
  }

  const supabase = getSupabaseBrowserClient();
  const extension = safeStorageExtension(input.file.name, "jpg");
  const path = `${input.userId}/sidebar-banner-${createId()}.${extension}`;
  const { error } = await supabase.storage
    .from(SITE_MEDIA_BUCKET)
    .upload(path, input.file, {
      cacheControl: "3600",
      contentType: input.file.type,
      upsert: false,
    });

  if (error) {
    throw error;
  }

  return path;
}

export async function removeSidebarBannerImage(path: string | null) {
  if (!path) {
    return;
  }

  const supabase = getSupabaseBrowserClient();
  await supabase.storage.from(SITE_MEDIA_BUCKET).remove([path]);
}

export async function setAdminSidebarBanner(input: {
  imagePath: string | null;
  targetUrl: string | null;
  altText: string | null;
  isActive: boolean;
}) {
  const supabase = getSupabaseBrowserClient();
  const { error } = await supabase.rpc("set_admin_sidebar_banner", {
    next_image_path: input.imagePath,
    next_target_url: input.targetUrl,
    next_alt_text: input.altText,
    next_is_active: input.isActive,
  });

  if (error) {
    throw error;
  }
}
